import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { applyVesselChanges, type ApplyVesselChangesInput } from '../api/applyVesselChanges';
import { fetchVesselAlerts, fetchVesselChanges } from '../api/vesselChangesQueries';
import { closeVesselAlert, dismissVesselChange } from '../api/vesselChangesMutations';
import { vesselChangesQueryKeys } from '../queryKeys';
import type { DismissVesselChangeRequest, VesselChangeList, VesselChangeScope } from '../types/vesselChanges';

export const useVesselChangeList = (scope: VesselChangeScope) =>
  useQuery({
    queryKey: vesselChangesQueryKeys.list(scope),
    queryFn: () => fetchVesselChanges(scope),
    refetchOnWindowFocus: true,
  });

/**
 * Open vessel alerts for the bell and the banner, polled every minute. A failure shows
 * nothing rather than an error: the alerts are a reminder on top of the page itself.
 */
export const useVesselAlerts = (enabled: boolean) =>
  useQuery({
    queryKey: vesselChangesQueryKeys.alerts(),
    queryFn: fetchVesselAlerts,
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
    retry: false,
  });

const withoutJob = (list: VesselChangeList | undefined, request: DismissVesselChangeRequest): VesselChangeList | undefined => {
  if (!list) return list;
  const groups = list.groups
    .map((group) => (group.call.id !== request.ptpVesselCallId ? group : {
      ...group,
      jobs: group.jobs.filter((job) => !(job.saleOrderId === request.saleOrderId && job.leg === request.leg)),
    }))
    .filter((group) => group.jobs.length > 0);
  return { ...list, groups };
};

/** Dismiss removes the row at once; the list and the alerts (both under `all`) are then read again. */
export const useDismissVesselChange = (scope: VesselChangeScope) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: dismissVesselChange,
    onSuccess: (_answer, request) => {
      queryClient.setQueryData<VesselChangeList>(vesselChangesQueryKeys.list(scope), (list) => withoutJob(list, request));
      void queryClient.invalidateQueries({ queryKey: vesselChangesQueryKeys.all });
    },
  });
};

/** After an update the list and the alerts (both under `all`) are read again. */
export const useApplyVesselChanges = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ApplyVesselChangesInput) => applyVesselChanges(input),
    onSettled: () => queryClient.invalidateQueries({ queryKey: vesselChangesQueryKeys.all }),
  });
};

/** "Not this vessel" on a LINK alert; the alerts are then read again. */
export const useCloseVesselAlert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: closeVesselAlert,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: vesselChangesQueryKeys.alerts() }),
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Could not close the alert'),
  });
};
